import { Flame, Plus } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { SimpleList } from "../simple-list/SimpleList";
import { Avatar } from "../contacts/Avatar";
import {
  LeadScoreBadge,
  computeLeadScore,
  useOpenDealContactIds,
} from "../contacts/leadScore";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";

/**
 * Hot contacts - ranked by native lead score, not just a manual "hot" flag.
 * People who keep engaging (email, repeat visits, recency) climb the list
 * on their own; the score rules live under Marketing → Workflows.
 */
export const HotContacts = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const { scoringRules } = useConfigurationContext();
  const openDealIds = useOpenDealContactIds();
  const { data: contacts = [], isPending: contactsLoading } =
    useGetList<Contact>(
      "contacts",
      {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "last_seen", order: "DESC" },
        filter: { sales_id: identity?.id },
      },
      { enabled: Number.isInteger(identity?.id) },
    );

  const ranked = useMemo(
    () =>
      contacts
        .map((c) => ({
          contact: c,
          score: computeLeadScore(c, scoringRules ?? [], openDealIds),
        }))
        .sort((a, b) => b.score.score - a.score.score)
        .slice(0, 8),
    [contacts, scoringRules, openDealIds],
  );
  const scoreOf = new Map(ranked.map((r) => [r.contact.id, r.score]));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Flame className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.contacts.hot.title", { _: "Hot Contacts" })}
        </h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-muted-foreground"
                asChild
              >
                <Link to="/contacts/create">
                  <Plus className="w-4 h-4 text-primary" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {translate("resources.contacts.action.create")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Card className="py-0">
        <SimpleList<Contact>
          linkType="show"
          data={ranked.map((r) => r.contact)}
          total={ranked.length}
          isPending={contactsLoading}
          resource="contacts"
          className="[&>li:first-child>a]:rounded-t-xl [&>li:last-child>a]:rounded-b-xl"
          primaryText={(contact) => (
            <span className="inline-flex items-center gap-2">
              {contact.first_name} {contact.last_name}
              {scoreOf.get(contact.id) && (
                <LeadScoreBadge score={scoreOf.get(contact.id)!} size="sm" />
              )}
            </span>
          )}
          secondaryText={(contact) => (
            <>
              {contact.title && contact.company_name
                ? translate("resources.contacts.position_at_company", {
                    title: contact.title,
                    company: contact.company_name,
                  })
                : contact.title || contact.company_name}
            </>
          )}
          leftAvatar={(contact) => <Avatar record={contact} />}
          empty={
            <div className="p-4">
              <p className="text-sm mb-4">
                {translate("resources.contacts.hot.empty_hint")}
              </p>
              <p className="text-sm">
                {translate("resources.contacts.hot.empty_change_status")}
              </p>
            </div>
          }
        />
      </Card>
      <p className="text-[11px] text-muted-foreground px-1">
        Ranked by lead score - engagement pushes people up automatically.
      </p>
    </div>
  );
};
